import type { User } from "../auth/models/user.model";
import type { Permission } from "../auth/models/permission.model";

function collectPermissionKeys(user: User | null): Set<string> {
  const keys = new Set<string>();
  if (!user) return keys;

  if (user.isSuperAdmin) {
    keys.add("*");
    return keys;
  }

  for (const role of user.roles ?? []) {
    for (const permission of role.permissions ?? []) {
      const key =
        typeof permission === "string"
          ? permission
          : permission?.key;
      if (!key) continue;
      keys.add(key);
      if (typeof permission !== "string" && permission.action === "manage") {
        keys.add(`${permission.resource}.read`);
        keys.add(`${permission.resource}.create`);
        keys.add(`${permission.resource}.update`);
        keys.add(`${permission.resource}.delete`);
      }
    }
  }
  return keys;
}

export function getUserPermissionKeys(user: User | null): string[] {
  return Array.from(collectPermissionKeys(user));
}

function permissionKeyVariants(permissionKey: string): string[] {
  const variants = new Set<string>([permissionKey]);
  if (!permissionKey.startsWith("hr.")) {
    variants.add(`hr.${permissionKey}`);
  } else {
    variants.add(permissionKey.slice(3));
  }
  return [...variants];
}

export function hasPermission(
  user: User | null,
  permissionKey: string
): boolean {
  const keys = collectPermissionKeys(user);
  if (keys.has("*")) return true;

  for (const variant of permissionKeyVariants(permissionKey)) {
    if (keys.has(variant)) return true;
    const [resource] = variant.split(".");
    if (keys.has(`${resource}.manage`)) return true;
  }

  return false;
}

export function hasAnyPermission(
  user: User | null,
  permissionKeys: string[]
): boolean {
  return permissionKeys.some((key) => hasPermission(user, key));
}

export function hasAllPermissions(
  user: User | null,
  permissionKeys: string[]
): boolean {
  return permissionKeys.every((key) => hasPermission(user, key));
}

export function hasRole(user: User | null, roleSlug: string): boolean {
  if (!user?.roles?.length) return false;
  return user.roles.some((r) => r.slug === roleSlug);
}

export function hasAnyRole(user: User | null, roleSlugs: string[]): boolean {
  return roleSlugs.some((slug) => hasRole(user, slug));
}

/** Compat legacy resource/action */
export function hasResourcePermission(
  user: User | null,
  resource: string,
  action: Permission["action"] = "read"
): boolean {
  return hasPermission(user, `${resource}.${action}`);
}
